import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { DropdownOption, DropdownProps } from "./types";
import { classNames } from "helpers";
import { GetDropdownItem, initialDropdownItem } from "./consts";
import DropdownItem from "./DropdownItem";
import { Tooltip } from "ui/components/Tooltip";

const MENU_MAX_HEIGHT = 224; // max-h-56
const MENU_OFFSET = 4;

const Dropdown: React.FC<DropdownProps> = (props: DropdownProps) => {
  const {
    name,
    required,
    disabled,
    label,
    error,
    value,
    direction,
    options,
    placeholder,
    width,
    height = "normal",
    onChange,
    cssClassName,
    theme = "light",
    isLoading
  } = props;

  const [selectedItem, setSelectedItem] = useState<DropdownOption>({
    ...initialDropdownItem,
    text: placeholder ?? initialDropdownItem.text,
  });

  const [open, setOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Popis se renderira u portalu jer ga scroll kontejneri (npr. ModalBody s overflow-y-auto)
  // inače režu; pozicija se računa iz okvira trigger elementa.
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const updateMenuPosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp =
      direction === "up" || (spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow);

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + MENU_OFFSET }
        : { top: rect.bottom + MENU_OFFSET }),
    });
  }, [direction]);

  useEffect(() => {
    if (!open) return;

    updateMenuPosition();

    // Fixed pozicija ne prati scroll pretka — capture faza hvata scroll bilo kojeg kontejnera.
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);

    return () => {
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [open, updateMenuPosition]);

  // If value is selected, don't show error (it will clear on next form validation)
  const hasValue = value !== undefined && value !== null && value !== "";
  const isError = !!error && !hasValue;

  const handleOpen = () => setOpen(!open);

  useEffect(() => {
    let item = GetDropdownItem(options, value, placeholder);
    setSelectedItem(item);
  }, [value, options]);

  const handleClicked = useCallback(
    (event: React.MouseEvent<HTMLElement>, value: string) => {
      event.preventDefault();

      let item = GetDropdownItem(options, value, placeholder);

      setSelectedItem(item);
      onChange(event, name, item);
      setOpen(false);
    },
    [setSelectedItem, onChange, setOpen, options]
  );

  // Nova funkcija za zatvaranje dropdowna klikom van njega
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;

      // Popis je u portalu (izvan wrappera) — bez ove provjere bi se uklonio na mousedown
      // prije nego klik na stavku uopće okine.
      if (wrapperRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;

      setOpen(false);
    },
    []
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, handleClickOutside]);

  const widthClassName = () => {
    switch (width) {
      case "min":
        return "w-24";
      case "small":
        return "w-48";
      case "medium":
        return "w-96";
      case "large":
        return "w-full";
      case "full":
        return "w-full";
      default:
        return "w-full";
    }
  };

  const heightClassName = () => {
    switch (height) {
      case "small":
        return "py-2";
      case "normal":
      default:
        return "py-3";
    }
  };

  const commonClassName = () => {
    const baseStyle = classNames("relative cursor-default rounded-sm pl-3 pr-3 text-left sm:text-sm sm:leading-6 z-0 focus:outline-none", heightClassName());

    if (disabled) {
      return classNames(baseStyle, "ring-1 ring-slate-300 bg-gray-100 text-gray-900 cursor-default");
    }

    if (isError && required) {
      return classNames(baseStyle, "ring-1 ring-red-500 bg-red-50 text-red-600 placeholder-red-700 cursor-pointer");
    }

    if (theme === "dark") {
      return classNames(baseStyle, "ring-1 ring-[#6b6b6b] bg-[#2a2c2f] text-white cursor-pointer");
    }

    return classNames(baseStyle, "ring-1 ring-slate-300 bg-slate-50 text-gray-800 placeholder-gray-400 focus:ring-slate-400 cursor-pointer");
  };

  const selectedItemClassName = () => {
    let result;

    if (isError && required && !disabled) {
    }
    else {
      if (theme === "light") {
        if (selectedItem.value === "") {
          result = "text-gray-400";
        }
        else {
          result = "text-gray-800";
        }
      }
    }

    return classNames("block truncate w-full pr-4 text-sm", result);
  };

  return (
    <>
      {label && (
        <label
          id="listbox-label"
          className="block text-sm mb-2 font-medium leading-6 text-[#344054]"
        >
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}

      <div
        ref={wrapperRef}
        // Dropdown ne renderira <input>, pa se po ovome pronalazi kod skrola na polje s greškom.
        data-field={name}
        className={classNames(widthClassName(), "relative min-w-full ")}
      >
        <button
          type="button"
          className={classNames(
            commonClassName(),
            widthClassName(),
            cssClassName
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby="listbox-label"
          onClick={handleOpen}
          disabled={disabled}
        >
          <span className="flex items-center ">
            {selectedItem?.color && (
              <span
                className="h-2.5 w-2.5 block mr-2 flex-shrink-0 rounded-full border border-slate-200"
                style={{ backgroundColor: selectedItem.color }}
              />
            )}
            {selectedItem?.image && !selectedItem?.color && (
              <img
                src={selectedItem?.image}
                alt=""
                className="h-5 w-5 block mr-3 flex-shrink-0 rounded-full"
              />
            )}

            <span className={selectedItemClassName()}>
              {selectedItem?.text}
            </span>

          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
            {isLoading ? (
              <svg
                aria-hidden="true"
                className="w-4 h-4 text-gray-200 animate-spin fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </span>
        </button>
        {open &&
          createPortal(
            <ul
              ref={listRef}
              style={menuStyle}
              className="z-[60] max-h-56 overflow-auto rounded-sm bg-white text-base shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none focus:ring-2 focus:ring-slate-400 sm:text-sm"
              tabIndex={-1}
              role="listbox"
              aria-labelledby="listbox-label"
              aria-activedescendant="listbox-option-3"
            >
              <DropdownItem
                text={placeholder ?? initialDropdownItem.text}
                value={initialDropdownItem.value}
                onClick={handleClicked}
                label={label}
                isPlaceholder
              />
              {options.map((item: DropdownOption, index: number) => {
                return (
                  <DropdownItem
                    label={label}
                    key={index}
                    text={item.text}
                    value={item.value}
                    image={item.image}
                    color={item.color}
                    onClick={handleClicked}
                  />
                );
              })}
            </ul>,
            document.body
          )}
        {isError && !disabled && (
          <div className="absolute right-2 top-2/4 -translate-y-1/2 ">
            <Tooltip description={error} />
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(Dropdown);
